/**
 * History feature module (W2-10 projects-data; DESIGN.md §4.18, §4.21, §5.13; WORKPACKAGES Appendix B: `history.*`
 * and the version commands of `project.*`).
 *
 * Provides:
 *  - the History dock tab (`chart.dock`, panel id 'history');
 *  - `DIALOG.versions` (Version history: restore as one undoable step, open as copy, checkpoints, budget);
 *  - commands: `project.checkpoint` (Ctrl+S in both presets: a checkpoint now, with the "autosave is always on"
 *    toast whose "Name it" names that very checkpoint), `project.nameCheckpoint` (`{ projectId?, seq? }`: the
 *    Versions dialog with that version's name field ready; without arguments the chart's current state),
 *    `project.versions`, `history.show`, `history.toggleJumpToChange`, `history.clear` (Full level);
 *  - a help topic about undo, history and versions;
 *  - `init`: remembers when this tab started using each project's store (the panel's "opened" divider).
 */

import { DIALOG } from '../../app/dialogs/types';
import type {
  CommandContext,
  CommandDef,
  FeatureModule,
  HelpTopicDef,
  KeyBinding,
  KeymapPresetId,
} from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import type { ProjectId } from '../../core/model/types';
import { m } from './history.messages';
import { HistoryPanel } from './HistoryPanel';
import { requestNaming } from './naming';
import { noteSession } from './session';
import { VersionsDialog } from './VersionsDialog';

function projectOpen(ctx: CommandContext): true | MessageRef {
  return ctx.store !== null ? true : { key: m.reasonNoProject };
}

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(err);
}

export const HISTORY_COMMANDS: readonly CommandDef[] = [
  {
    id: 'project.checkpoint',
    title: m.cmdCheckpoint,
    category: 'File',
    icon: 'BookmarkPlus',
    level: 'guided',
    aliases: ['save', 'checkpoint', 'snapshot', 'version', 'Ctrl+S', '保存', '存档'],
    hint: 1,
    menus: [{ menu: 'app.project', group: 'versions', order: 10 }],
    enabled: projectOpen,
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      let seq: number;
      try {
        await ctx.persist.flush();
        seq = (await ctx.persist.checkpoint(store.projectId, null, 'checkpoint')).seq;
      } catch (err) {
        ctx.ui.toast({ key: m.checkpointFailed, params: { reason: errorText(err) } }, { kind: 'error' });
        return;
      }
      ctx.ui.toast(
        { key: m.checkpointToast },
        {
          kind: 'success',
          action: {
            label: { key: m.nameIt },
            command: 'project.nameCheckpoint',
            args: { projectId: store.projectId, seq },
          },
        },
      );
    },
  },
  {
    id: 'project.nameCheckpoint',
    title: m.cmdNameCheckpoint,
    category: 'File',
    icon: 'PenLine',
    level: 'guided',
    aliases: ['name checkpoint', 'rename checkpoint', 'name version', 'label version', '命名存档'],
    menus: [{ menu: 'app.project', group: 'versions', order: 15 }],
    enabled: projectOpen,
    async run(ctx, args) {
      const store = ctx.store;
      if (store === null) return;
      const a = obj(args);
      const projectId = typeof a.projectId === 'string' && a.projectId !== '' ? (a.projectId as ProjectId) : null;
      const seq = typeof a.seq === 'number' && Number.isInteger(a.seq) && a.seq >= 0 ? a.seq : null;
      const target = projectId ?? store.projectId;
      // Without a seq: the current state of the open chart (another chart's state is not known here).
      const at = seq ?? (target === store.projectId ? store.seq : null);
      if (at !== null) requestNaming(ctx.persist, target, { seq: at, justMade: seq !== null });
      await ctx.ui.openDialog(DIALOG.versions, { projectId: target });
    },
  },
  {
    id: 'project.versions',
    title: m.cmdVersions,
    category: 'File',
    icon: 'History',
    level: 'guided',
    aliases: ['versions', 'version history', 'restore', 'backup', 'snapshots', 'checkpoints', '历史版本', '版本'],
    menus: [{ menu: 'app.project', group: 'versions', order: 20 }],
    enabled: projectOpen,
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      await ctx.ui.openDialog(DIALOG.versions, { projectId: store.projectId });
    },
  },
  {
    id: 'history.show',
    title: m.cmdShow,
    category: 'View',
    icon: 'History',
    level: 'guided',
    aliases: ['history', 'undo history', 'undo list', '历史记录', '撤销'],
    menus: [{ menu: 'app.view', group: 'panels', order: 30 }],
    enabled: projectOpen,
    run(ctx) {
      ctx.ui.showPanel('history');
    },
  },
  {
    id: 'history.toggleJumpToChange',
    title: m.cmdJumpToChange,
    category: 'Edit',
    icon: 'Crosshair',
    level: 'guided',
    aliases: ['jump to change', 'follow undo', 'reveal undo'],
    checked: (ctx) => ctx.settings.value.editing.jumpToChangeOnUndo,
    run(ctx) {
      ctx.settings.set('editing', 'jumpToChangeOnUndo', !ctx.settings.value.editing.jumpToChangeOnUndo);
    },
  },
  {
    id: 'history.clear',
    title: m.cmdClear,
    category: 'Edit',
    icon: 'ListX',
    level: 'full',
    aliases: ['clear history', 'free memory', 'forget undo', '清除历史'],
    enabled(ctx) {
      const store = ctx.store;
      if (store === null) return { key: m.reasonNoProject };
      if (store.history.entries().length === 0) return { key: m.reasonNothingToClear };
      return true;
    },
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const ok = await ctx.ui.confirm({
        title: { key: m.clearTitle },
        body: { key: m.clearBody, params: { count: store.history.entries().length } },
        confirm: { key: m.clearConfirm },
        danger: true,
      });
      if (!ok) return;
      store.clearHistory();
      ctx.ui.toast({ key: m.cleared }, { kind: 'success' });
    },
  },
];

const checkpointKey: KeyBinding = { chord: 'Ctrl+KeyS', command: 'project.checkpoint' };

export const HISTORY_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [checkpointKey],
  rpe: [checkpointKey],
};

const HELP_TOPICS: readonly HelpTopicDef[] = [
  {
    id: 'history',
    title: m.helpTitle,
    body: m.helpBody,
    level: 'guided',
    showMe: { panel: 'history' },
  },
];

export default {
  id: 'history',
  commands: HISTORY_COMMANDS,
  keybindings: HISTORY_KEYBINDINGS,
  panels: [
    {
      id: 'history',
      title: m.panelTitle,
      icon: 'History',
      slot: 'chart.dock',
      order: 30,
      level: 'guided',
      component: HistoryPanel,
    },
  ],
  dialogs: [{ id: DIALOG.versions, kind: 'modal', component: VersionsDialog }],
  helpTopics: HELP_TOPICS,
  init(s) {
    const note = (): void => {
      if (s.store !== null) noteSession(s.store);
    };
    note();
    return s.onChange(note);
  },
} satisfies FeatureModule;
